import React, { useMemo, useState } from 'react';

import { Empty, Layout, Select, Spin, Button } from 'antd';

import styled from 'styled-components';

import { getThumbnailWidth, getVideoCover } from '@libs/NwUtils';

import { useContactPicturesAndVideos } from '@hooks/API/Contact/UseContactMedia';
import { getContactName } from '@hooks/API/Contact/UseContactsList';
import useDataPlaceholder from '@hooks/Utils/UseDataPlaceholder';

import BookerSelector from '@components/BookerSelector/BookerSelector';
import FilterDateInput from '@components/Filters/FilterDateInput';
import CustomSwitch from '@components/Gui/CustomSwitch';
import FlexContainer from '@components/Gui/FlexContainer';
import MediaCover from '@components/Gui/MediaCover';
import { NwCancelButton, NwSaveButton } from '@components/Gui/NwButton';
import NwButtonOption from '@components/Gui/NwButtonOption';
import NwDrawer from '@components/Gui/NwDrawer';
import InputTag from '@components/Gui/NwForm/InputTag';
import NwIcon from '@components/Gui/NwIcon';
import StyleVariables from '@components/Gui/StyleVariables';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const ButtonGroup = Button.Group;
const { Option } = Select;

const HeaderContainer = styled.div`
    margin: 1rem;
    padding: 1em;
    background: #fff;
    border-radius: 6px;
    border: ${(props) => props.theme.LighterBorder};
    display: flex;
    flex-direction: row;
    font-size: 0.9rem;

    @media only screen and (max-width: 960px) {
        flex-direction: column;
    }

    .info-block {
        display: flex;
        flex-direction: row;
        border-right: ${StyleVariables.DefaultBorder};
        padding-right: 20px;

        @media only screen and (max-width: 960px) {
            border-right: none;
        }

        .model-info {
            .model-name {
                font-size: 2rem;
                font-weight: 600;
            }
        }
    }

    .filter-block {
        @media only screen and (max-width: 960px) {
            margin: 10px 0;
            padding: 10px 0;
            border-top: ${StyleVariables.DefaultBorder};
            border-bottom: ${StyleVariables.DefaultBorder};
        }
    }
`;

const FilterContainer = styled.div`
    margin: 1rem;
    padding: 1em;
    background: #fff;
    border-radius: 6px;

    .filter-row {
        display: flex;
        flex-direction: row;

        @media only screen and (max-width: 960px) {
            flex-direction: column;
        }

        .individual-filter {
            padding-right: 30px;
            width: 400px;

            @media only screen and (max-width: 960px) {
                width: 100%;
                padding-right: 0;
                margin-bottom: 1rem;
            }
        }
    }
`;

const Label = styled.div`
    margin-right: 8px;
    min-width: 50px;
`;

const ListContainer = styled.div`
    margin: 0 1rem 6rem 1rem;
    display: flex;
    flex-direction: column;
`;

const MediaGalleryContent = styled.div`
    display: flex;
    flex-wrap: wrap;

    .image-block {
        color: white;
        flex: 1 0 auto;
        position: relative;
        margin: 0.5rem;
        cursor: pointer;
    }

    &.media-gallery-content-large,
    &.media-gallery-content-xlarge {
        .image-block {
            margin: 0.75rem;
        }
    }

    .count-badge {
        width: 30px;
        height: 30px;
        border-radius: 100%;
        background: ${StyleVariables.PrimaryColor};
        position: absolute;
        top: -10px;
        display: flex;
        justify-content: center;
        margin: auto;
        flex-direction: column;
        align-items: center;
        font-weight: bold;

        .icon {
            display: none;
        }

        &:hover {
            background: ${StyleVariables.DangerColor};

            .number {
                display: none;
            }

            .icon {
                display: flex;
            }
        }
    }
`;

const FooterContainer = styled.div`
    z-index: 100;
    position: fixed;
    bottom: 0;
    left: 10vw;
    background-color: #fff;
    padding: 16px 24px;
    box-sizing: border-box;
    width: 90vw;
    font-weight: bold;
    text-transform: uppercase;
`;

const getRowHeight = (size) => {
    switch (size) {
        case 'small':
            return 160;
        case 'large':
            return 300;
        case 'medium':
        default:
            return 230;
    }
};

const getMediaRating = (media, type) => {
    if (!media.Rating) {
        return 0;
    }
    if (type === 'myrating') {
        return media.Rating.currentUserRating || 0;
    }
    return media.Rating.averageRating || 0;
};

const getMediaUrl = (document) => {
    if (document.Type === 'Video') {
        return document.Standard && document.Standard.Url ? document.Standard.Url : getVideoCover(document);
    }
    return document.Standard && document.Standard.Url ? document.Standard.Url : null;
};

const convertDocument = (document, size) => {
    const thumbnailHeight = getRowHeight(size);

    if (document.Type === 'Photo') {
        if (!document.Standard || !document.Standard.Url) {
            return null;
        }
        const thumbnailWidth = getThumbnailWidth(document, thumbnailHeight) || thumbnailHeight;
        return {
            ID: document.ID,
            caption: document.Title,
            disabled: false,
            fullUrl: getMediaUrl(document),
            original: document,
            selected: Boolean(document.isSelected),
            src: document.Standard.Url,
            thumbnail: document.Thumbnail && document.Thumbnail.Url ? document.Thumbnail.Url : document.Standard.Url,
            thumbnailHeight,
            thumbnailWidth,
            type: document.Type,
            url: getMediaUrl(document),
        };
    }

    if (document.Type === 'Video') {
        const videoCover = getVideoCover(document);
        if (!videoCover) {
            return null;
        }
        return {
            ID: document.ID,
            caption: document.Title,
            disabled: false,
            fullUrl: getMediaUrl(document),
            original: document,
            selected: Boolean(document.isSelected),
            src: videoCover,
            thumbnail: videoCover,
            thumbnailHeight,
            thumbnailWidth: thumbnailHeight * 1.5,
            type: document.Type,
            url: getMediaUrl(document),
        };
    }

    return null;
};

const NwMediaSelectorHeader = ({
    documents,
    includePictures,
    includeVideos,
    loading,
    modelId,
    onChangeSize,
    onChangeSort,
    onChangeUploadedSince,
    onChangeUploadedUntil,
    onChangeUploader,
    onUpdateShowMedia,
    onUpdateTags,
    showMedia,
    size,
    sort,
    tags,
    uploadedSince,
    uploadedUntil,
    uploader,
}) => {
    const [showFilters, setShowFilters] = useState(false);
    const photoCount = documents.filter((document) => document.Type === 'Photo').length;
    const videoCount = documents.filter((document) => document.Type === 'Video').length;

    const handleUpdateShowMedia = (type, value) => {
        onUpdateShowMedia({
            ...showMedia,
            [type]: value,
        });
    };

    return (
        <>
            <HeaderContainer>
                <div className="info-block">
                    <div className="model-info">
                        <div className="model-name">{getContactName(modelId)}</div>
                        {loading ? <Spin spinning /> : <div>{`${photoCount} PICTURES, ${videoCount} VIDEOS`}</div>}
                    </div>
                </div>
                <div className="filter-block">
                    <FlexContainer mg="0 0 10px 16px">
                        <Label>Size</Label>
                        <ButtonGroup size="small" style={{ zIndex: '0' }}>
                            <NwButtonOption active={size === 'small'} onClick={onChangeSize('small')}>
                                S
                            </NwButtonOption>
                            <NwButtonOption active={size === 'medium'} onClick={onChangeSize('medium')}>
                                M
                            </NwButtonOption>
                            <NwButtonOption active={size === 'large'} onClick={onChangeSize('large')}>
                                L
                            </NwButtonOption>
                        </ButtonGroup>
                    </FlexContainer>
                    <FlexContainer>
                        <FlexContainer mg="0 16px">
                            <Label>Sort By</Label>
                            <Select autoComplete="off" style={{ width: 160 }} value={sort} onChange={onChangeSort}>
                                <Option value="time">time</Option>
                                <Option value="myrating">my rating</Option>
                                <Option value="globalrating">global rating</Option>
                            </Select>
                        </FlexContainer>
                        <CustomSwitch
                            checked={showFilters}
                            onChange={() => setShowFilters(!showFilters)}
                            text="Filter"
                        />
                    </FlexContainer>
                </div>
            </HeaderContainer>
            {showFilters && (
                <FilterContainer>
                    <FlexContainer mg="0 0 20px 0">
                        {includePictures && (
                            <CustomSwitch
                                checked={showMedia.isShowPictures}
                                onChange={(value) => handleUpdateShowMedia('isShowPictures', value)}
                                text="Pictures"
                            />
                        )}
                        {includeVideos && (
                            <CustomSwitch
                                checked={showMedia.isShowVideos}
                                onChange={(value) => handleUpdateShowMedia('isShowVideos', value)}
                                text="Videos"
                                style={{ marginLeft: includePictures ? '30px' : 0 }}
                            />
                        )}
                    </FlexContainer>
                    <div className="filter-row" style={{ marginBottom: '20px' }}>
                        <div className="individual-filter">
                            <div>Uploaded By</div>
                            <BookerSelector value={uploader} style={{ width: '100%' }} onChange={onChangeUploader} />
                        </div>
                        <div className="individual-filter">
                            <div>Tags</div>
                            <InputTag value={tags} contactType="Document" onChange={onUpdateTags} />
                        </div>
                    </div>
                    <div className="filter-row">
                        <div className="individual-filter">
                            <div>Uploaded Since</div>
                            <FilterDateInput
                                allowClear
                                value={uploadedSince}
                                style={{ width: '100%' }}
                                onChange={onChangeUploadedSince}
                            />
                        </div>
                        <div className="individual-filter">
                            <div>Uploaded Until</div>
                            <FilterDateInput
                                allowClear
                                value={uploadedUntil}
                                style={{ width: '100%' }}
                                onChange={onChangeUploadedUntil}
                            />
                        </div>
                    </div>
                </FilterContainer>
            )}
        </>
    );
};

const NwMediaSelectorList = ({ documents, multiple, onSelectMedia, size, sort }) => {
    const [selectedMediaList, setSelectedMediaList] = useState([]);

    const sortedMediaList = useMemo(() => {
        const sortedList = [...documents];
        if (sort === 'time') {
            sortedList.sort((item1, item2) => (new Date(item2.DateCreated) > new Date(item1.DateCreated) ? 1 : -1));
        } else if (sort === 'myrating' || sort === 'globalrating') {
            sortedList.sort((item1, item2) => getMediaRating(item2, sort) - getMediaRating(item1, sort));
        }
        return sortedList;
    }, [documents, sort]);

    const documentList = useMemo(() => {
        return sortedMediaList.map((item) => convertDocument(item, size)).filter(Boolean);
    }, [size, sortedMediaList]);

    const getSourceUrl = (item) => {
        if (size === 'large') {
            return item.src;
        }
        return item.thumbnail;
    };

    const handleDeselectMedia = (media) => {
        const index = selectedMediaList.findIndex((item) => item.ID === media.ID);
        const nextSelectedMediaList = [...selectedMediaList];
        nextSelectedMediaList.splice(index, 1);
        setSelectedMediaList(nextSelectedMediaList);
    };

    const handleSelectMedia = (media) => {
        if (!multiple) {
            onSelectMedia(media);
            return;
        }
        const index = selectedMediaList.findIndex((item) => item.ID === media.ID);
        if (index >= 0) {
            handleDeselectMedia(media);
            return;
        }
        setSelectedMediaList([...selectedMediaList, media]);
    };

    const handleSaveSelection = () => {
        if (!selectedMediaList.length) {
            return;
        }
        onSelectMedia(selectedMediaList);
    };

    const handleClearSelection = () => {
        setSelectedMediaList([]);
    };

    const renderBadge = (media) => {
        const index = selectedMediaList.findIndex((item) => item.ID === media.ID);
        if (index === -1) {
            return null;
        }
        return (
            <div
                className="count-badge"
                style={{ left: `${media.thumbnailWidth - 5}px` }}
                onClick={() => handleDeselectMedia(media)}
            >
                <div className="number">{index + 1}</div>
                <div className="icon">
                    <NwIcon icon={byPrefixAndName.fasl['times']} />
                </div>
            </div>
        );
    };

    return (
        <>
            <ListContainer>
                {documentList.length ? (
                    <MediaGalleryContent className={`media-gallery-content-${size}`}>
                        {documentList.map((item) => (
                            <div key={item.ID} className="image-block">
                                <MediaCover
                                    type={item.type}
                                    selected={selectedMediaList.some((media) => media.ID === item.ID)}
                                    selectable={false}
                                    src={getSourceUrl(item)}
                                    onClick={() => handleSelectMedia(item)}
                                    style={{
                                        minWidth: item.thumbnailWidth + 'px',
                                        maxWidth: item.thumbnailWidth + 10 + 'px',
                                        width: item.thumbnailWidth + 'px',
                                        height: item.thumbnailHeight + 'px',
                                    }}
                                />
                                {multiple && renderBadge(item)}
                            </div>
                        ))}
                    </MediaGalleryContent>
                ) : (
                    <Empty description="No media" />
                )}
            </ListContainer>
            {multiple && (
                <FooterContainer>
                    <FlexContainer fullWidth justifyContent="space-between">
                        <div>{selectedMediaList.length} selected media</div>
                        <FlexContainer>
                            <NwSaveButton
                                disabled={selectedMediaList.length === 0}
                                onClick={handleSaveSelection}
                                style={{ marginRight: '20px' }}
                            />
                            <NwCancelButton onClick={handleClearSelection} />
                        </FlexContainer>
                    </FlexContainer>
                </FooterContainer>
            )}
        </>
    );
};

const NwMediaSelector = ({
    includePictures = true,
    includeVideos = true,
    modelId,
    multiple = false,
    onClose,
    onSelectMedia,
    title = 'Select media',
    zIndex,
}) => {
    const replacePlaceHolder = useDataPlaceholder();
    const { data: documents, isFetching } = useContactPicturesAndVideos(modelId);
    const [size, setSize] = useState('medium');
    const [sort, setSort] = useState('time');
    const [showMedia, setShowMedia] = useState({
        isShowPictures: includePictures,
        isShowVideos: includeVideos,
    });
    const [tags, setTags] = useState([]);
    const [uploader, setUploader] = useState();
    const [uploadedSince, setUploadedSince] = useState();
    const [uploadedUntil, setUploadedUntil] = useState();

    const handleUpdateSize = (value) => () => {
        setSize(value);
    };

    const displayedDocuments = useMemo(() => {
        let documentList = [];
        if (documents && documents.length) {
            documentList = documents.filter((document) => {
                if (document.Type === 'Photo' && includePictures && showMedia.isShowPictures) {
                    return true;
                }
                if (document.Type === 'Video' && includeVideos && showMedia.isShowVideos) {
                    return true;
                }
                return false;
            });

            if (uploader) {
                documentList = documentList.filter((document) => document.UserCreated === uploader);
            }

            documentList = documentList.filter((document) => {
                const documentTags = document.Tags || [];
                for (const tag of tags) {
                    if (!documentTags.find((item) => item.ID === tag)) {
                        return false;
                    }
                }
                return true;
            });

            const uploadedSinceDate = replacePlaceHolder(uploadedSince);
            const uploadedUntilDate = replacePlaceHolder(uploadedUntil);
            documentList = documentList.filter((document) => {
                if (uploadedSince && new Date(uploadedSinceDate) > new Date(document.DateCreated)) {
                    return false;
                }
                if (uploadedUntil && new Date(uploadedUntilDate) < new Date(document.DateCreated)) {
                    return false;
                }
                return true;
            });
        }
        return documentList;
    }, [
        documents,
        includePictures,
        includeVideos,
        replacePlaceHolder,
        showMedia,
        tags,
        uploadedSince,
        uploadedUntil,
        uploader,
    ]);

    return (
        <NwDrawer width="90vw" title={title} zIndex={zIndex} onClose={onClose} padding={0}>
            <Layout style={{ minHeight: '100vh' }}>
                <NwMediaSelectorHeader
                    documents={documents || []}
                    includePictures={includePictures}
                    includeVideos={includeVideos}
                    loading={isFetching}
                    modelId={modelId}
                    size={size}
                    sort={sort}
                    onChangeSort={setSort}
                    onChangeSize={handleUpdateSize}
                    showMedia={showMedia}
                    onUpdateShowMedia={setShowMedia}
                    tags={tags}
                    onUpdateTags={setTags}
                    uploader={uploader}
                    onChangeUploader={setUploader}
                    uploadedSince={uploadedSince}
                    onChangeUploadedSince={setUploadedSince}
                    uploadedUntil={uploadedUntil}
                    onChangeUploadedUntil={setUploadedUntil}
                />
                <NwMediaSelectorList
                    documents={displayedDocuments}
                    multiple={multiple}
                    size={size}
                    sort={sort}
                    onSelectMedia={onSelectMedia}
                />
            </Layout>
        </NwDrawer>
    );
};

export default NwMediaSelector;
